import { Button, Modal, PaymentSummary, StickyContainer } from "@covertree/ui";
import { InfoDisclaimerBox } from "@covertree/ui/content";

import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { Addresses, MailingAddressForm } from "./Addresses";
import { ContactDetails, ContactDetailsForm } from "./ContactDetails";
import { PlanDetails } from "./PlanDetails";
import { ProceedButton } from "./ProceedButton";
import { QuoteTooltip } from "./QuoteTooltip";
import { InternalTrackingSubcode } from "./InternalTrackingSubcode";
import { usePolicySummary } from "./hooks/usePolicySummary";

interface Props {
  isD2C: boolean;
  useNewOffersAndSelectedQuotePricing: boolean;
  contactNumber?: string;
  onCloseMailingAddressReminder(): void;
  onContactDetailsSubmit(data: ContactDetailsForm): Promise<void>;
  onContinue(): void;
  onEditMyPlan(): void;
  onMailingAddressSubmit(data: MailingAddressForm): Promise<void>;
  onSaveInternalTrackingSubcode?(subcode: string): Promise<void>;
}

export function PolicySummary({
  isD2C,
  useNewOffersAndSelectedQuotePricing,
  contactNumber,
  onCloseMailingAddressReminder,
  onContactDetailsSubmit,
  onContinue,
  onEditMyPlan,
  onMailingAddressSubmit,
  onSaveInternalTrackingSubcode,
}: Props) {
  const {
    formattedStartDate,
    isDesktop,
    policyLevelCoverages,
    policyholder,
    pricing,
    showMailingAddressReminder,
    units,
    formattedPolicyUsage,
  } = usePolicySummary({ useNewOffersAndSelectedQuotePricing });

  const pageTitle = isD2C ? (
    <Typography
      data-testid="policy-summary-header"
      variant={isDesktop ? "h5" : "h6"}
      mb={4}>
      Feel free to personalize your plan, then check out when ready.
    </Typography>
  ) : (
    <Typography data-testid="policy-summary-header" variant="h4" mb={2}>
      Take a moment to review your policy
    </Typography>
  );

  const paymentSummary = (
    <PaymentSummary pricing={pricing} formattedStartDate={formattedStartDate} />
  );

  const proceedButton = <ProceedButton onContinue={onContinue} />;

  const quoteTooltip = isD2C && !!contactNumber && (
    <QuoteTooltip contactNumber={contactNumber} />
  );

  const internalTrackingSubcodeButton =
    !isD2C && onSaveInternalTrackingSubcode ? (
      <InternalTrackingSubcode
        onSaveInternalTrackingSubcode={onSaveInternalTrackingSubcode}
      />
    ) : null;

  return (
    <>
      <Grid container spacing={2} sx={{ mt: isD2C ? 0.2 : -2 }}>
        <Grid item xs={12} md={8} pt={0}>
          {pageTitle}
          <Stack spacing={2}>
            <InfoDisclaimerBox dataTestId="disclaimer-box-summary" mt={3}>
              Click <strong>Edit my plan</strong> below to update coverages,
              limits, and pricing in real-time for a custom plan.
            </InfoDisclaimerBox>
            {!!policyholder.hasActivePolicies && (
              <InfoDisclaimerBox dataTestId="disclaimer-box" mt={3}>
                Some policyholder data below cannot be edited due to at least
                one active policy assigned to them.
              </InfoDisclaimerBox>
            )}
            {!isDesktop && paymentSummary}
            {!isDesktop && quoteTooltip}
            <Addresses
              onMailingAddressSubmit={onMailingAddressSubmit}
              propertyAddress={units[0].address}
              policyholder={policyholder}
            />
            <ContactDetails
              isD2C={isD2C}
              policyholder={policyholder}
              onContactDetailsSubmit={onContactDetailsSubmit}
            />
            <PlanDetails
              formattedStartDate={formattedStartDate}
              onEditMyPlan={onEditMyPlan}
              policyLevelCoverages={policyLevelCoverages}
              policyPlan={units[0].selectedPlan}
              unitLevelCoverages={units[0].unitLevelCoverages}
            />
            {!isDesktop && proceedButton}
            {!isDesktop && internalTrackingSubcodeButton}
            <Stack direction="column" pt={1}>
              <Typography
                variant="subtitle1"
                fontStyle="italic"
                sx={{ color: "action.disabled" }}>
                Quote is subject to underwriter guidelines and approval, and
                therefore is subject to change.
              </Typography>
            </Stack>
          </Stack>
        </Grid>
        {isDesktop && (
          <Grid item xs={4} container>
            <Grid item xs={12} justifyContent="center">
              <StickyContainer topOffset={isD2C ? 16 : 120} isD2C={isD2C}>
                {paymentSummary}
                {proceedButton}
                {internalTrackingSubcodeButton}
                {quoteTooltip}
              </StickyContainer>
            </Grid>
          </Grid>
        )}
      </Grid>
      {!isD2C && (
        <Modal
          maxWidth="sm"
          open={!!showMailingAddressReminder}
          onClose={onCloseMailingAddressReminder}
          title="Notice!">
          <Typography sx={{ mt: -3, mb: 3 }}>
            Selected occupancy type is{" "}
            <Typography component="span" color="secondary">
              {formattedPolicyUsage}
            </Typography>
            .
            <Typography>
              Please double check that the mailing address is correct.
            </Typography>
          </Typography>
          <Button
            onClick={onCloseMailingAddressReminder}
            data-testid="close-button">
            Close
          </Button>
        </Modal>
      )}
    </>
  );
}
